import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { customerBaseUrl } from './runtime-env';

// 后端地址：指向 customer BFF（其 HTTP 端点内部经 gRPC 调 auth 引擎）。
// 基址在运行时解析（Workers vars 注入 → 回落 environment），见 runtime-env.ts。
export const api = (p: string): string => `${customerBaseUrl()}${p}`;

// SSR 安全的存储访问（服务端渲染时没有 localStorage/sessionStorage）。
const hasLocal = typeof localStorage !== 'undefined';
const hasSession = typeof sessionStorage !== 'undefined';
const lsGet = (k: string): string | null => (hasLocal ? localStorage.getItem(k) : null);
const lsSet = (k: string, v: string): void => {
  if (hasLocal) localStorage.setItem(k, v);
};
const lsDel = (k: string): void => {
  if (hasLocal) localStorage.removeItem(k);
};

// 登录结果：ok=已拿到正式令牌；mfa=启用 TOTP，需带 ticket 做二次校验。
export interface LoginOutcome {
  status: 'ok' | 'mfa';
  mfaTicket?: string;
  mfaEnrolled: boolean;
}

interface LoginResponse {
  token?: string;
  refresh_token?: string;
  roles?: string[];
  user?: { name?: string; email?: string };
  mfa_required?: boolean;
  mfa_ticket?: string;
  mfa_enrolled?: boolean;
}

// PeekInvitationReply 注册页回显（后端只回邮箱与有效性，不含敏感信息）。
export interface PeekInvitationReply {
  valid: boolean;
  email: string;
  expires_at: number;
  // 无效原因：not_found / expired / used / revoked
  reason: string;
}

export interface SecurityStatus {
  has_totp: boolean;
  has_passkey: boolean;
  mfa_enrolled: boolean;
}

export interface PasskeyItem {
  id: string;
  name: string;
  created_at: number;
  last_used_at: number;
}

export interface AuditLogItem {
  action: string;
  result: string;
  reason: string;
  created_at: number;
  metadata: Record<string, string>;
}

export interface AuditLogPage {
  items: AuditLogItem[];
  total: number;
  page: number;
  page_size: number;
}

@Injectable({ providedIn: 'root' })
export class Auth {
  private http = inject(HttpClient);
  private i18n = inject(TranslateService);

  private _token = signal<string | null>(lsGet('token'));
  private _user = signal<string | null>(lsGet('user'));
  private _mfaEnrolled = signal<boolean>(lsGet('mfa_enrolled') === '1');
  readonly token = this._token.asReadonly();
  readonly user = this._user.asReadonly();
  readonly mfaEnrolled = this._mfaEnrolled.asReadonly();

  isAuthenticated(): boolean {
    return !!this._token();
  }
  currentToken(): string | null {
    return this._token();
  }

  // ── 密码登录 ──
  // peekInvitation 凭邀请码回显被邀请邮箱（公开端点，注册页未登录时调）。
  async peekInvitation(code: string): Promise<PeekInvitationReply> {
    return firstValueFrom(
      this.http.get<PeekInvitationReply>(api('/v1/customer/auth/invitation'), { params: { code } }),
    );
  }

  // register 邀请注册。invite_code 来自邀请邮件链接的 ?code=，不由用户手输。
  async register(email: string, password: string, name: string, inviteCode: string): Promise<LoginOutcome> {
    const res = await firstValueFrom(
      this.http.post<LoginResponse>(api('/v1/customer/auth/register'), {
        email,
        password,
        name,
        invite_code: inviteCode,
      }),
    );
    return this.applyLogin(res, email);
  }

  async login(username: string, password: string): Promise<LoginOutcome> {
    const res = await firstValueFrom(
      this.http.post<LoginResponse>(api('/v1/customer/auth/login'), { username, password }),
    );
    return this.applyLogin(res, username);
  }

  // applyLogin 处理任意登录响应：mfa 待校验则不落令牌；否则落库并刷新会话信号。
  private applyLogin(res: LoginResponse, fallbackName?: string): LoginOutcome {
    if (res.mfa_required && res.mfa_ticket) {
      return { status: 'mfa', mfaTicket: res.mfa_ticket, mfaEnrolled: !!res.mfa_enrolled };
    }
    if (!res.token) {
      throw new Error(this.i18n.instant('pgMisc.loginNoToken'));
    }
    const name = res.user?.name ?? res.user?.email ?? fallbackName ?? '';
    lsSet('token', res.token);
    if (res.refresh_token) lsSet('refresh_token', res.refresh_token);
    lsSet('user', name);
    lsSet('mfa_enrolled', res.mfa_enrolled ? '1' : '0');
    this._token.set(res.token);
    this._user.set(name);
    this._mfaEnrolled.set(!!res.mfa_enrolled);
    return { status: 'ok', mfaEnrolled: !!res.mfa_enrolled };
  }

  // ── 登录二次因素（TOTP/恢复码）──
  async mfaLoginVerify(ticket: string, code: string): Promise<LoginOutcome> {
    const res = await firstValueFrom(
      this.http.post<LoginResponse>(api('/v1/customer/auth/mfa/login/verify'), {
        mfa_ticket: ticket,
        code,
      }),
    );
    return this.applyLogin(res);
  }

  // ── OAuth 一键登录（重定向到 provider；redirect_uri 由后端配置指向 /auth/callback）──
  async oauthAuthorize(provider: 'google' | 'github'): Promise<void> {
    const res = await firstValueFrom(
      this.http.get<{ authorize_url: string; state: string }>(
        api(`/v1/customer/auth/oauth/authorize?provider=${provider}`),
      ),
    );
    if (hasSession) sessionStorage.setItem('oauth_provider', provider);
    if (typeof location !== 'undefined') location.href = res.authorize_url;
  }

  async handleOAuthCallback(code: string, state: string): Promise<LoginOutcome> {
    const provider = (hasSession && sessionStorage.getItem('oauth_provider')) || 'google';
    const res = await firstValueFrom(
      this.http.get<LoginResponse>(
        api(
          `/v1/customer/auth/oauth/callback?provider=${provider}&code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
        ),
      ),
    );
    if (hasSession) sessionStorage.removeItem('oauth_provider');
    return this.applyLogin(res);
  }

  // ── Passkey 登录（无用户名 discoverable）──
  // ⚠️ Passkey 的 begin/finish 必须【拆开】—— 这不是洁癖,是 Safari 的硬性要求。
  //
  // Safari 规定 navigator.credentials.get()/create() 必须在【用户手势的有效期内】被调用。
  // 如果在点击回调里先 await 一个网络请求(拿 challenge),手势就过期了,Safari 直接抛
  // NotAllowedError —— 而 Chrome 宽松,照样能用。所以 Chrome 好使、Safari 一直失败。
  //
  // 解法:页面加载时【预取】challenge,点击时【立刻】调 WebAuthn(中间不能有任何 await)。
  passkeyLoginBegin(): Promise<{ options_json: string; session_id: string }> {
    return firstValueFrom(
      this.http.post<{ options_json: string; session_id: string }>(
        api('/v1/customer/auth/passkey/login/begin'),
        {},
      ),
    );
  }

  // 必须在点击回调里【同步】调用(不要 await 任何东西之后再调)。
  passkeyAssert(begin: { options_json: string }): Promise<unknown> {
    const opts = JSON.parse(begin.options_json);
    return startAuthentication({ optionsJSON: opts.publicKey });
  }

  async passkeyLoginFinish(sessionId: string, assertion: unknown): Promise<LoginOutcome> {
    const res = await firstValueFrom(
      this.http.post<LoginResponse>(api('/v1/customer/auth/passkey/login/finish'), {
        session_id: sessionId,
        credential_json: JSON.stringify(assertion),
      }),
    );
    return this.applyLogin(res);
  }

  // ── Passkey 绑定/管理（需登录）──
  // 同 passkeyLogin:begin/finish 必须拆开,否则 Safari 因用户手势过期而拒绝(见上方说明)。
  passkeyRegisterBegin(): Promise<{ options_json: string; session_id: string }> {
    return firstValueFrom(
      this.http.post<{ options_json: string; session_id: string }>(
        api('/v1/customer/auth/passkey/register/begin'),
        {},
      ),
    );
  }

  // 必须在点击回调里【同步】调用。
  passkeyAttest(begin: { options_json: string }): Promise<unknown> {
    const opts = JSON.parse(begin.options_json);
    return startRegistration({ optionsJSON: opts.publicKey });
  }

  async passkeyRegisterFinish(sessionId: string, att: unknown, name: string): Promise<void> {
    await firstValueFrom(
      this.http.post(api('/v1/customer/auth/passkey/register/finish'), {
        session_id: sessionId,
        credential_json: JSON.stringify(att),
        name,
      }),
    );
    this.markEnrolled();
  }

  listPasskeys(): Promise<{ passkeys: PasskeyItem[] }> {
    return firstValueFrom(
      this.http.get<{ passkeys: PasskeyItem[] }>(api('/v1/customer/auth/passkeys')),
    );
  }
  deletePasskey(id: string): Promise<unknown> {
    return firstValueFrom(
      this.http.delete(api(`/v1/customer/auth/passkeys?id=${encodeURIComponent(id)}`)),
    );
  }

  // ── TOTP 绑定/状态/关闭 ──
  totpEnrollBegin(): Promise<{ secret: string; otpauth_uri: string }> {
    return firstValueFrom(
      this.http.post<{ secret: string; otpauth_uri: string }>(
        api('/v1/customer/auth/totp/enroll/begin'),
        {},
      ),
    );
  }
  async totpEnrollFinish(code: string): Promise<{ recovery_codes: string[] }> {
    const r = await firstValueFrom(
      this.http.post<{ recovery_codes: string[] }>(api('/v1/customer/auth/totp/enroll/finish'), {
        code,
      }),
    );
    this.markEnrolled();
    return r;
  }
  totpStatus(): Promise<{ enabled: boolean; confirmed_at: number }> {
    return firstValueFrom(
      this.http.get<{ enabled: boolean; confirmed_at: number }>(api('/v1/customer/auth/totp')),
    );
  }
  totpDisable(code: string): Promise<unknown> {
    return firstValueFrom(
      this.http.request('DELETE', api('/v1/customer/auth/totp'), { body: { code } }),
    );
  }

  // ── 安全状态 ──
  getSecurityStatus(): Promise<SecurityStatus> {
    return firstValueFrom(this.http.get<SecurityStatus>(api('/v1/customer/auth/security')));
  }

  // ── 操作日志（本人审计记录）──
  listAuditLogs(page: number, pageSize: number): Promise<AuditLogPage> {
    return firstValueFrom(
      this.http.get<AuditLogPage>(
        api(`/v1/customer/auth/audit-logs?page=${page}&page_size=${pageSize}`),
      ),
    );
  }

  // ── 交易 step-up：换取与本笔参数绑定的一次性令牌 ──
  stepUpVerify(p: {
    code: string;
    from_account: string;
    to_account: string;
    amount: number;
    currency: string;
  }): Promise<{ step_up_token: string; expires_at: number }> {
    return firstValueFrom(
      this.http.post<{ step_up_token: string; expires_at: number }>(
        api('/v1/customer/auth/stepup/verify'),
        p,
      ),
    );
  }

  // ── refresh：供拦截器在 401 时静默续期 ──
  async refresh(): Promise<boolean> {
    const rt = lsGet('refresh_token');
    if (!rt) return false;
    try {
      const res = await firstValueFrom(
        this.http.post<LoginResponse>(api('/v1/customer/auth/token/refresh'), {
          refresh_token: rt,
        }),
      );
      if (res.token) {
        lsSet('token', res.token);
        if (res.refresh_token) lsSet('refresh_token', res.refresh_token);
        this._token.set(res.token);
        return true;
      }
    } catch {
      /* 刷新失败：交由调用方走登出/跳登录 */
    }
    return false;
  }

  logout(): void {
    lsDel('token');
    lsDel('refresh_token');
    lsDel('user');
    lsDel('mfa_enrolled');
    this._token.set(null);
    this._user.set(null);
    this._mfaEnrolled.set(false);
  }

  private markEnrolled(): void {
    lsSet('mfa_enrolled', '1');
    this._mfaEnrolled.set(true);
  }

  // 依据服务端真实 MFA 状态刷新本地标记（signal + localStorage）；守卫复核用，避免本地陈旧标记误跳转。
  applyMfaEnrolled(enrolled: boolean): void {
    lsSet('mfa_enrolled', enrolled ? '1' : '0');
    this._mfaEnrolled.set(enrolled);
  }

  // userId 从当前 JWT 的 sub 解出用户 id（账户/换汇接口的 path 参数）。SSR/无 token 返回空串。
  userId(): string {
    const t = this._token();
    if (!t) return '';
    try {
      const seg = t.split('.')[1];
      const json = JSON.parse(atob(seg.replace(/-/g, '+').replace(/_/g, '/')));
      return String(json.sub ?? '');
    } catch {
      return '';
    }
  }
}
